<script setup lang="ts">
import { CloseOutlined, PlusOutlined } from "@ant-design/icons-vue";
import { FormItemRest, useInjectFormItemContext } from "ant-design-vue/es/form";
import { NoFormStatus } from "ant-design-vue/es/form/FormItemContext";
import { computed, getCurrentInstance, onMounted, ref, useAttrs, watch } from "vue";
import { buildSelectedUserMap, fetchSelectedAdminList } from "./shared";
import type { SelectedUser } from "./types";
import UserSelectModal from "./UserSelectModal.vue";

interface UserSelectProps {
	placeholder?: string;
	disabled?: boolean;
	multiple?: boolean;
	maxCount?: number;
}

const props = withDefaults(defineProps<UserSelectProps>(), {
	multiple: false,
	maxCount: 0,
});

// 组件对外暴露的Model绑定值：单选返回number，多选返回number[]
type ValueType = typeof props.multiple extends true ? number[] : number;
const value = defineModel<ValueType>();
const formItemContext = useInjectFormItemContext();
const canNotifyFormItem = ref(false);

// 内部使用的用户列表，包含id和realname（以及Model绑定的用户数据）
const modelValue = ref<SelectedUser[]>([]);
const selectedUserMap = ref<Map<number, SelectedUser>>(new Map());

const loadSelectedUserMap = async () => {
	if (selectedUserMap.value.size > 0) {
		return;
	}

	const adminList = await fetchSelectedAdminList();

	selectedUserMap.value = buildSelectedUserMap(adminList);
};

const getIdsFromValue = (rawValue: ValueType | undefined) => {
	if (props.multiple) {
		return Array.isArray(rawValue) ? rawValue : [];
	}

	return typeof rawValue === "number" ? [rawValue] : [];
};

const isSameUsers = (left: SelectedUser[], right: SelectedUser[]) => {
	if (left.length !== right.length) {
		return false;
	}

	return left.every(
		(item, index) => item.id === right[index]?.id && item.realname === right[index]?.realname
	);
};

const resolveUsersByIds = (ids: number[]) => {
	return ids.map((id) => selectedUserMap.value.get(id) ?? { id, realname: String(id) });
};

watch(
	() => value.value,
	async (newValue) => {
		if (canNotifyFormItem.value) {
			formItemContext.onFieldChange();
		}

		const ids = getIdsFromValue(newValue);

		if (ids.length === 0) {
			if (modelValue.value.length > 0) {
				modelValue.value = [];
			}

			return;
		}

		await loadSelectedUserMap();

		const nextModelValue = resolveUsersByIds(ids);

		if (!isSameUsers(modelValue.value, nextModelValue)) {
			modelValue.value = nextModelValue;
		}
	},
	{ immediate: true }
);

watch(
	() => modelValue.value,
	(newVal) => {
		const ids = newVal.map((user) => user.id);
		const nextValue = (props.multiple ? ids : ids[0]) as ValueType;

		if (Array.isArray(value.value) && Array.isArray(nextValue)) {
			const sameLength = value.value.length === nextValue.length;
			const sameIds = sameLength && value.value.every((id, index) => id === nextValue[index]);

			if (sameIds) {
				return;
			}
		}

		if (!Array.isArray(value.value) && !Array.isArray(nextValue) && value.value === nextValue) {
			return;
		}

		value.value = nextValue;
	},
	{ deep: true }
);

onMounted(() => {
	canNotifyFormItem.value = true;
});

const open = ref(false);

// 组件大小适配
const attrs = useAttrs();
const instance = getCurrentInstance();

const normalizeSize = (size: unknown): "small" | "middle" | "large" | undefined => {
	if (size === "small" || size === "middle" || size === "large") {
		return size;
	}

	if (size === "default") {
		return "middle";
	}

	return undefined;
};

const currentSize = computed<"small" | "middle" | "large">(() => {
	const vnodeProps = (instance?.vnode.props ?? {}) as Record<string, unknown>;
	const attrsRecord = attrs as Record<string, unknown>;
	const inject = (attrsRecord.formCreateInject ??
		vnodeProps.formCreateInject ??
		attrsRecord.ctx ??
		vnodeProps.ctx ??
		{}) as Record<string, unknown>;

	const formOptions = ((inject.api as { options?: { form?: { size?: unknown } } } | undefined)
		?.options ?? (inject.options as { form?: { size?: unknown } } | undefined)) as
		| { form?: { size?: unknown } }
		| undefined;

	const size =
		normalizeSize(attrsRecord.size) ??
		normalizeSize(vnodeProps.size) ??
		normalizeSize(formOptions?.form?.size) ??
		normalizeSize(inject.size);

	return size ?? "middle";
});

const itemClass = computed(() => {
	switch (currentSize.value) {
		case "small":
			return "rounded-sm h-lg py-ty";
		case "large":
			return "rounded-md h-hs py-ts";
		default:
			return "rounded h-lh py-ts";
	}
});

const tagClass = computed(() => {
	switch (currentSize.value) {
		case "small":
			return "rounded-sm text-sm leading-ms";
		case "large":
			return "rounded-md text-base leading-mms";
		default:
			return "rounded text-sm leading-ms";
	}
});

const handleOpen = () => {
	if (props.disabled) {
		return;
	}

	open.value = true;
};

const handleRemove = (id: number) => {
	if (props.disabled) {
		return;
	}

	modelValue.value = modelValue.value.filter((user) => user.id !== id);
};
</script>

<template>
	<div
		class="border-border gap-sm px-ts flex h-full w-full flex-wrap items-center border border-solid transition-colors"
		:class="{
			[itemClass]: true,
			'focus-within:border-primary hover:border-primary bg-white': !props.disabled,
			'bg-white-ash cursor-not-allowed': props.disabled,
		}"
	>
		<div
			v-for="user in modelValue"
			:key="user.id"
			class="px-sm bg-driver gap-ty flex h-full cursor-pointer items-center"
			:class="tagClass"
		>
			<span>{{ user.realname }}</span>
			<CloseOutlined v-if="!disabled" class="cursor-pointer" @click="handleRemove(user.id)" />
		</div>

		<div
			v-if="!disabled"
			class="px-sm bg-driver gap-ty flex h-full cursor-pointer items-center"
			:class="tagClass"
			@click="handleOpen"
		>
			<PlusOutlined />
			<span>添加</span>
		</div>

		<FormItemRest>
			<NoFormStatus>
				<UserSelectModal
					v-model:open="open"
					v-model="modelValue"
					:multiple="props.multiple"
					:max-count="props.maxCount"
				/>
			</NoFormStatus>
		</FormItemRest>
	</div>
</template>
